/* eslint-disable react/display-name */
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { GridColDef } from "@material-ui/data-grid";

import BaseDataGrid from "../../../../../components/modules/BaseDataGrid";
import ConfirmationModal from "../../../../../components/modules/ConfirmationModal";
import LineItemsReportDashboardToolbar from "./LineItemsReportDashboardToolbar";
import Loader from "../../../../../components/elements/Loader";
import Switch from "../../../../../components/elements/Switch";
import api from "../../../../../api";
import classes from "../../CampaignsDashboard.module.scss";
import {
  CampaignLinteItemType,
  CampaignType,
  LineItemReportType,
  ReportTimeFrames,
} from "../../../../../types/campaignTypes";
import { RowType, transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { floatingNumbersFields, metricsColumns } from "../../constants";
import { truncateText } from "../../../../../helpers/truncateText";

const fetchLineItemsWithReport = async (brandgenCampaignId: string, selectedTimeFrame: string) => {
  try {
    const { payload } = await api.getLineItemReports(brandgenCampaignId, selectedTimeFrame);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

const fetchLineItemsDailyReport = async (brandgenCampaignId: string, date: Date) => {
  try {
    const { payload } = await api.getLineItemDailyReport(brandgenCampaignId, date);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

type CampaignLineItemsReportDashboardProps = {
  selectedTimeFrame: ReportTimeFrames;
  campaign: CampaignType;
  customReportDate: Date | undefined;
  isCustomDateChanged: boolean;
};

type LineItemToUpdate = {
  id: number;
  active: boolean;
};

const CampaignLineItemsReportDashboard: FC<CampaignLineItemsReportDashboardProps> = ({
  selectedTimeFrame,
  campaign,
  customReportDate,
  isCustomDateChanged,
}) => {
  const [lineItems, setLineItems] = useState<CampaignLinteItemType[]>([]);
  const [lineItemsReport, setLineItemsReport] = useState<LineItemReportType[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sourceHeaderName, setSourceHeaderName] = useState<string>("Source");

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [lineItemToUpdate, setLineItemToUpdate] = useState<LineItemToUpdate | null>(null);

  const handleOpenConfirmationModal = () => setIsConfirmationModalOpen(true);
  const handleCloseConfirmationModal = () => setIsConfirmationModalOpen(false);

  const clearStates = useCallback(() => {
    setLineItems([]);
    setLineItemsReport([]);
  }, []);

  useEffect(() => {
    const fetchAndSetLineItemReports = async () => {
      try {
        setIsLoading(true);

        let response;
        if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate) {
          if (!isCustomDateChanged) {
            setIsLoading(false);
            return;
          }
          response = await fetchLineItemsDailyReport(campaign.brandgenCampaignId, customReportDate);
        } else {
          response = await fetchLineItemsWithReport(campaign.brandgenCampaignId, selectedTimeFrame);
        }

        if (!response) {
          clearStates();
        } else if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE) {
          setLineItems(response.lineItems);
          setLineItemsReport(response.lineItemReports);
        } else {
          setLineItems(response);
        }
      } catch (error) {
        console.error("fetchAndSetLineItemReports error: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetLineItemReports();
  }, [
    campaign.brandgenCampaignId,
    selectedTimeFrame,
    campaign.active,
    customReportDate,
    isCustomDateChanged,
    clearStates,
  ]);

  useEffect(() => {
    if (lineItems && lineItems.length > 0) {
      const headerName = !lineItems[0].hubspotCompanyId ? "File Upload" : "Source";
      setSourceHeaderName(headerName);
    } else {
      setSourceHeaderName("Source");
    }
  }, [lineItems]);

  const updateLineItemActiveStatus = async () => {
    try {
      if (!lineItemToUpdate) return;

      setIsLoading(true);
      await api.updateLineItemStatus(lineItemToUpdate.id, !lineItemToUpdate.active);
      const fetchedLineItemReports = await fetchLineItemsWithReport(campaign.brandgenCampaignId, selectedTimeFrame);
      setLineItems(fetchedLineItemReports);
      setIsLoading(false);
      setLineItemToUpdate(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("updateLineItemActiveStatus error: ", error);
      setIsLoading(false);
    }
  };

  const handleLineItemActiveStatusChange = useCallback(
    (id: number, active: boolean) => () => {
      handleOpenConfirmationModal();
      setLineItemToUpdate({ id, active });
    },
    [],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "active",
        headerName: "Status",
        width: 150,
        renderCell: (params: any) => (
          <React.Fragment key={params.row.beeswaxLineItemId}>
            <Typography className={params.row.active === "ACTIVE" ? classes.activeStatus : classes.inactiveStatus}>
              {params.row.active}
            </Typography>
            <Switch
              checked={params.row.active === "ACTIVE"}
              onChange={handleLineItemActiveStatusChange(params.row.beeswaxLineItemId, params.row.active === "ACTIVE")}
            />
          </React.Fragment>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        width: 220,
        renderCell: ({ value }) => (
          <Tooltip title={value as string}>
            <Typography>{truncateText(value as string, 25)}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "source",
        headerName: sourceHeaderName,
        width: 200,
      },
      {
        field: "hsCompanyId",
        headerName: "HS Company Id",
        width: 180,
      },
      ...metricsColumns,
    ],
    [handleLineItemActiveStatusChange, sourceHeaderName],
  );

  const rows = useMemo(() => {
    return lineItems.map((item, index) => {
      const { beeswaxLineItemId, line_item_name, active, hubspotCompanyId, fromFile, lineItemReports } = item;

      const report =
        selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE
          ? lineItemsReport.find((r) => r.beeswaxLineItemId === beeswaxLineItemId)
          : lineItemReports?.[0];

      const baseRow: { [key: string]: any } = {
        id: beeswaxLineItemId.toString() + index,
        hsCompanyId: hubspotCompanyId ?? "N/A",
        source: hubspotCompanyId ? "hs" : fromFile ?? "file",
        beeswaxLineItemId,
        name: line_item_name ?? "N/A",
        active: active ? "ACTIVE" : "INACTIVE",
      };

      if (report) {
        for (const metric of metricsColumns) {
          const value = report[metric.field as keyof LineItemReportType] as number;
          const isFloat = floatingNumbersFields.includes(metric.field);
          baseRow[metric.field] = transformRowValue(value, metric.type === "FLAT", isFloat, metric.type as RowType);
        }
      } else {
        metricsColumns.forEach((metric) => {
          baseRow[metric.field] = "N/A";
        });
      }

      return baseRow;
    });
  }, [lineItems, lineItemsReport, selectedTimeFrame]);

  const isMaxRowsAmountExceeded = rows.length > 10;

  return (
    <div
      className={`${classes.lineItemsDashboard} ${
        isMaxRowsAmountExceeded ? classes.lineItemsDashboardFixedHeight : ""
      }`}
    >
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        handleClose={handleCloseConfirmationModal}
        confirmQuestion="Are you sure you want to update this line item's status?"
        confirmButtonLabel="Yes"
        handleConfirm={updateLineItemActiveStatus}
      />
      {isLoading ? (
        <Loader className={classes.loader} isAbsolutePosition={false} size={42} />
      ) : (
        <>
          <BaseDataGrid
            components={{
              Toolbar: LineItemsReportDashboardToolbar,
            }}
            localeText={{ toolbarColumns: "Select Metrics" }}
            autoHeight={!isMaxRowsAmountExceeded}
            columns={columns}
            rows={rows.some((row) => Object.keys(row).length > 0) ? rows : []}
          />
        </>
      )}
    </div>
  );
};

export default CampaignLineItemsReportDashboard;
